import React, { useContext, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import productData from "../Datas/productData";
import Footer from "./Footer";
import "../css/productdetails.scss";
import { CartContext } from "../Component/CartContext";
import Header from "./Header";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === parseInt(id));
  const { addToCart } = useContext(CartContext);

  const [selectedSize, setSelectedSize] = useState(product?.size?.[0] || "");
  const [selectedColor, setSelectedColor] = useState(
    product?.colors?.[0] || ""
  );
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [descOpen, setDescOpen] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!product) {
    return (
      <div className="product-detail">
        <Header />
        <div className="product-detail__not-found">
          <h2>Product not found</h2>
          <Link onClick={toTop} to="/shop">
            Back to Shop
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    navigate("/cartpage");
    toTop();
  };

  const incrementQty = () => setQuantity((q) => q + 1);
  const decrementQty = () => setQuantity((q) => Math.max(1, q - 1));

  const featureList = product.Features || [];

  return (
    <div className="product-detail">
      <Header />

      <div className="product-detail__container">
        {/* Breadcrumb */}
        <nav className="product-detail__breadcrumb">
          <Link onClick={toTop} to="/shop">
            Home
          </Link>
          <span className="separator">/</span>
          <span className="current">{product.name}</span>
        </nav>

        <div style={{ display: "flex", gap: "30px" }}>
          {/* Image Gallery */}
          <div className="product-detail__gallery">
            <div className="gallery__thumbs">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  className={`thumb ${idx === selectedImage ? "active" : ""}`}
                  onClick={() => setSelectedImage(idx)}
                >
                  <img src={img} alt={`${product.name} view ${idx + 1}`} />
                </button>
              ))}
            </div>
            <div className="gallery__main">
              <img
                src={product.images[selectedImage]}
                alt={product.name}
                className="main-image"
              />
            </div>
          </div>

          {/* Product Info */}
          <div className="product-detail__info">
            <h1 className="product-title">{product.name}</h1>

            <div className="product-price">
              HK${Number(product.price).toLocaleString("en-US")}
            </div>

            <div className="product-features">
              {featureList.map((feature, idx) => (
                <div key={idx} className="feature-item">
                  <svg
                    className="check-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <div className="product-options">
              {/* Color */}
              <div className="option-group">
                <label className="option-label">
                  Color: <span className="option-value">{selectedColor}</span>
                </label>
                <div className="color-options">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      className={`color-btn ${
                        selectedColor === color ? "selected" : ""
                      }`}
                      onClick={() => setSelectedColor(color)}
                      title={color}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>

              {/* Size */}
              <div className="option-group">
                <label className="option-label">
                  Size: <span className="option-value">{selectedSize}</span>
                </label>
                <div className="size-options">
                  {product.size.map((size) => (
                    <button
                      key={size}
                      className={`size-btn ${
                        selectedSize === size ? "selected" : ""
                      }`}
                      onClick={() => setSelectedSize(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div className="option-group">
                <label className="option-label">Qty:</label>
                <div className="qty-selector">
                  <button className="qty-btn" onClick={decrementQty}>
                    -
                  </button>
                  <span className="qty-value">{quantity}</span>
                  <button className="qty-btn" onClick={incrementQty}>
                    +
                  </button>
                </div>
              </div>
            </div>

            <button className="add-to-cart-btn" onClick={handleAddToCart}>
              ADD TO CART
            </button>

            {/* Collapsible Sections */}
            <div className="product-accordions">
              <div className="accordion-item">
                <button
                  className="accordion-header"
                  onClick={() => setDescOpen(!descOpen)}
                >
                  <span>Description</span>
                  <svg
                    className={`chevron ${descOpen ? "open" : ""}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {descOpen && (
                  <div className="accordion-content">
                    {product.description.map((desc, idx) => (
                      <p key={idx}>{desc}</p>
                    ))}
                  </div>
                )}
              </div>

              <div className="accordion-item">
                <button
                  className="accordion-header"
                  onClick={() => setDetailsOpen(!detailsOpen)}
                >
                  <span>Details</span>
                  <svg
                    className={`chevron ${detailsOpen ? "open" : ""}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {detailsOpen && (
                  <div className="accordion-content">
                    <ul className="details-list">
                      <li>
                        <strong>Material:</strong> {product.material}
                      </li>
                      <li>
                        <strong>Category:</strong> {product.category}
                      </li>
                      {product.colors && (
                        <li>
                          <strong>Available Colors:</strong>{" "}
                          {product.colors.join(", ")}
                        </li>
                      )}
                      {product.Features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;
